import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SortIcon from "@mui/icons-material/Sort";
import { getInitials, getAvatarColor } from "../../utils/avatarUtils";
import { toast } from "react-toastify";

export default function CustomerMeasurements() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // Debounced search query
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false); // For pagination loading
  const [schemeCredits, setSchemeCredits] = useState({});
  const [sortBy, setSortBy] = useState("tokenNo"); // New sorting state
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [pageSize] = useState(10); // Customers per page
  const [openingBalanceDialogOpen, setOpeningBalanceDialogOpen] = useState(false);
  const [selectedCustomerForBalance, setSelectedCustomerForBalance] = useState(null);
  const [openingBalanceForm, setOpeningBalanceForm] = useState({
    type: "opening_balance", // "opening_balance" or "advance"
    amount: "",
  });
  
  // View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedCustomerForView, setSelectedCustomerForView] = useState(null);
  const [viewCustomerLoading, setViewCustomerLoading] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCustomerForDelete, setSelectedCustomerForDelete] = useState(null);

  // Fetch customer data from API
  useEffect(() => {
    fetchCustomers();
  }, [currentPage, searchQuery]); // Re-fetch when page or search changes

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(query);
      setCurrentPage(1); // Reset to first page when searching
    }, 500);

    return () => clearTimeout(timer);
  }, [query]);

  // Add a refresh function that can be called from other components
  useEffect(() => {
    const handleCustomerDataRefresh = () => {
      // Reset to first page and refresh
      setCurrentPage(1);
      setTimeout(() => {
        fetchCustomers();
      }, 200);
    };

    // Listen for custom events to refresh customer data
    window.addEventListener('refreshCustomerData', handleCustomerDataRefresh);
    
    // Also listen for storage events in case of cross-tab updates
    window.addEventListener('storage', (e) => {
      if (e.key === 'refreshCustomerData') {
        handleCustomerDataRefresh();
      }
    });
    
    return () => {
      window.removeEventListener('refreshCustomerData', handleCustomerDataRefresh);
      window.removeEventListener('storage', handleCustomerDataRefresh);
    };
  }, []);

  const fetchCustomers = async () => {
    try {
      // Show page loading for pagination, full loading for initial load
      if (currentPage === 1 && !searchQuery) {
        setLoading(true);
      } else {
        setPageLoading(true);
      }
      
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/customers?page=${currentPage}&limit=${pageSize}&search=${encodeURIComponent(searchQuery)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCustomers(data.data);
        setTotalPages(data.totalPages || 1);
        setTotalCustomers(data.total || 0);
        
        // Fetch scheme credits for current page customers only
        fetchAllSchemeCredits(data.data, token);
      } else {
        console.error("Failed to fetch customers");
        toast.error("Failed to load customers");
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Error loading customers");
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  const fetchAllSchemeCredits = async (customersList, token) => {
    const credits = {};
    
    // Fetch scheme credit for each customer
    await Promise.all(
      customersList.map(async (customer) => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/schemes/customer/${customer.TokenNo}/available-credit`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (response.ok) {
            const data = await response.json();
            credits[customer.TokenNo] = data.totalAvailable || 0;
          } else {
            credits[customer.TokenNo] = 0;
          }
        } catch (error) {
          console.error(`Error fetching scheme credit for customer ${customer.TokenNo}:`, error);
          credits[customer.TokenNo] = 0;
        }
      })
    );

    setSchemeCredits(credits);
  };

  // Since we're doing server-side filtering and pagination, we just need to sort the current page results
  const sortedCustomers = useMemo(() => {
    if (!customers || customers.length === 0) return [];
    
    // Apply sorting based on selected option
    return [...customers].sort((a, b) => {
      switch (sortBy) {
        case "tokenNo":
          return a.TokenNo - b.TokenNo;
        
        case "dueHighToLow": {
          const aDue = (parseFloat(a.CurrentBalance) || 0) - (parseFloat(schemeCredits[a.TokenNo]) || 0);
          const bDue = (parseFloat(b.CurrentBalance) || 0) - (parseFloat(schemeCredits[b.TokenNo]) || 0);
          return bDue - aDue; // High to low (descending)
        }
        
        case "dueLowToHigh": {
          const aDue = (parseFloat(a.CurrentBalance) || 0) - (parseFloat(schemeCredits[a.TokenNo]) || 0);
          const bDue = (parseFloat(b.CurrentBalance) || 0) - (parseFloat(schemeCredits[b.TokenNo]) || 0);
          return aDue - bDue; // Low to high (ascending)
        }
        
        case "nameAZ":
          return a.CustomerName.localeCompare(b.CustomerName);
        
        case "nameZA":
          return b.CustomerName.localeCompare(a.CustomerName);
        
        default:
          return a.TokenNo - b.TokenNo;
      }
    });
  }, [customers, schemeCredits, sortBy]);

  const handleView = async (customer) => {
    setSelectedCustomerForView(null);
    setViewDialogOpen(true);
    setViewCustomerLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/customers/token/${customer.TokenNo}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const result = await response.json();
        setSelectedCustomerForView({
          ...result.data,
          Birthday: result.data.Birthday ? result.data.Birthday.split("T")[0] : "",
          AnniversaryDate: result.data.AnniversaryDate ? result.data.AnniversaryDate.split("T")[0] : "",
        });
      } else {
        toast.error("Failed to fetch customer details");
        setViewDialogOpen(false);
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      toast.error("Failed to load customer details");
      setViewDialogOpen(false);
    } finally {
      setViewCustomerLoading(false);
    }
  };

  const handleEdit = (customer) => {
    navigate(`/admin/customers/edit/${customer.TokenNo}`);
  };

  const handleDelete = (customer) => {
    setSelectedCustomerForDelete(customer);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedCustomerForDelete) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/customers/${selectedCustomerForDelete.TokenNo}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        toast.success(`${selectedCustomerForDelete.CustomerName} deleted successfully`);
        fetchCustomers(); // Refresh the list
      } else {
        toast.error("Failed to delete customer");
      }
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error("Failed to delete customer");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedCustomerForDelete(null);
    }
  };

  const handleOpeningBalanceClick = (customer) => {
    setSelectedCustomerForBalance(customer);
    
    // Check if customer has existing opening balance or advance
    const hasOpeningBalance = customer.OpeningBalance && parseFloat(customer.OpeningBalance) !== 0;
    const hasAdvance = customer.Advance && parseFloat(customer.Advance) !== 0;
    
    if (hasOpeningBalance) {
      setOpeningBalanceForm({
        type: "opening_balance",
        amount: customer.OpeningBalance.toString(),
      });
    } else if (hasAdvance) {
      setOpeningBalanceForm({
        type: "advance", 
        amount: customer.Advance.toString(),
      });
    } else {
      setOpeningBalanceForm({
        type: "opening_balance",
        amount: "",
      });
    }
    
    setOpeningBalanceDialogOpen(true);
  };

  const handleOpeningBalanceSubmit = async () => {
    if (!selectedCustomerForBalance) return;

    try {
      const amount = parseFloat(openingBalanceForm.amount) || 0;
      const token = localStorage.getItem("token");
      
      let endpoint, body;
      
      if (openingBalanceForm.type === "opening_balance") {
        // Set opening balance (adds to customer due)
        endpoint = `${API_BASE_URL}/api/customers/${selectedCustomerForBalance.TokenNo}/opening-balance`;
        body = { amount };
      } else {
        // Record advance payment (subtracts from customer due)
        endpoint = `${API_BASE_URL}/api/customers/${selectedCustomerForBalance.TokenNo}/advance`;
        body = { amount };
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success(
          openingBalanceForm.type === "opening_balance" 
            ? "Opening balance updated successfully" 
            : "Advance payment recorded successfully"
        );
        fetchCustomers(); // Refresh the customer data
        setOpeningBalanceDialogOpen(false);
        setSelectedCustomerForBalance(null);
        setOpeningBalanceForm({ type: "opening_balance", amount: "" });
      } else {
        toast.error(
          openingBalanceForm.type === "opening_balance"
            ? "Failed to update opening balance"
            : "Failed to record advance payment"
        );
      }
    } catch (error) {
      console.error("Error updating balance:", error);
      toast.error("Failed to update balance");
    }
  };

  const handleDeleteBalance = async () => {
    if (!selectedCustomerForBalance) return;

    if (!window.confirm("Are you sure you want to delete this balance entry? This will reset the balance to zero.")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const endpoint = `${API_BASE_URL}/api/customers/${selectedCustomerForBalance.TokenNo}/balance`;

      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success("Balance entry deleted successfully");
        fetchCustomers(); // Refresh the customer data
        setOpeningBalanceDialogOpen(false);
        setSelectedCustomerForBalance(null);
        setOpeningBalanceForm({ type: "opening_balance", amount: "" });
      } else {
        toast.error("Failed to delete balance entry");
      }
    } catch (error) {
      console.error("Error deleting balance:", error);
      toast.error("Failed to delete balance entry");
    }
  };

  return (
    <AppLayout>
      <Box sx={{ 
        minHeight: "calc(100vh - 80px)", 
        background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)", 
        p: { xs: 2, md: 4 } 
      }}>
        <Box sx={{ maxWidth: 1400, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: "#C4A574", mb: 1 }}>
              CUSTOMERS
            </Typography>
            <Typography variant="body1" sx={{ color: "#bbb" }}>
              Manage customer information and measurements
            </Typography>
          </Box>

          {/* Search and Controls Section */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 3,
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <TextField
                fullWidth
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by Token / Name / Phone..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#C4A574" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#1a1a1a",
                    borderRadius: 1,
                    border: "1px solid rgba(196,165,116,0.06)",
                    color: "#fff",
                    "& fieldset": { borderColor: "transparent" },
                    "&:hover fieldset": { borderColor: "rgba(196,165,116,0.12)" },
                    "&.Mui-focused fieldset": { borderColor: "#C4A574" },
                  },
                  "& .MuiInputBase-input": { color: "#fff" },
                  "& .MuiInputBase-input::placeholder": { color: "#bbb", opacity: 1 },
                  "& .MuiFormLabel-root": { color: "#C4A574" },
                }}
              />
              
              {/* Sort Dropdown */}
              <FormControl sx={{ minWidth: { xs: "100%", sm: "200px" } }}>
                <InputLabel sx={{ color: "#C4A574" }}>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                  startAdornment={<SortIcon sx={{ color: "#C4A574", mr: 1 }} />}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#1a1a1a",
                      borderRadius: 1,
                      border: "1px solid rgba(196,165,116,0.06)",
                      color: "#fff",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(196,165,116,0.12)" },
                      "&.Mui-focused fieldset": { borderColor: "#C4A574" },
                    },
                    "& .MuiSelect-select": { color: "#fff" },
                    "& .MuiInputLabel-root": { color: "#C4A574" },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#1a1a1a",
                        "& .MuiMenuItem-root": {
                          color: "#fff",
                          "&:hover": { bgcolor: "#333" },
                          "&.Mui-selected": { bgcolor: "#C4A574", color: "#000" }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="tokenNo">Token Number</MenuItem>
                  <MenuItem value="dueHighToLow">Due Amount: High to Low</MenuItem>
                  <MenuItem value="dueLowToHigh">Due Amount: Low to High</MenuItem>
                  <MenuItem value="nameAZ">Name: A to Z</MenuItem>
                  <MenuItem value="nameZA">Name: Z to A</MenuItem>
                </Select>
              </FormControl>
              
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/admin/customers/new")}
                sx={{
                  bgcolor: "#C4A574",
                  color: "#000",
                  px: 3,
                  py: 1.5,
                  minWidth: { xs: "100%", sm: "180px" },
                  fontWeight: 700,
                  "&:hover": { bgcolor: "#B39562" },
                }}
              >
                Add Customer
              </Button>
            </Box>

            {/* Customer Cards Grid */}
            <Box sx={{ display: "flex", justifyContent: "center", position: "relative" }}>
              {pageLoading && (
                <Box sx={{ 
                  position: "absolute", 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  display: "flex", 
                  justifyContent: "center", 
                  alignItems: "center", 
                  bgcolor: "rgba(0,0,0,0.5)", 
                  zIndex: 1,
                  borderRadius: 2
                }}>
                  <CircularProgress sx={{ color: "#C4A574" }} />
                </Box>
              )}
              <Grid container spacing={3} sx={{ maxWidth: 1200, justifyContent: "center" }}>
                {loading ? (
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                      <CircularProgress sx={{ color: "#C4A574" }} />
                    </Box>
                  </Grid>
                ) : sortedCustomers && sortedCustomers.length > 0 ? (
                  sortedCustomers.map((customer) => {
                    const initials = getInitials(customer.CustomerName);
                    const avatarColor = getAvatarColor(customer.CustomerName);
                    
                    return (
                      <Grid item key={customer.TokenNo} xs={12} sm={6} md={4} lg={3} sx={{ display: "flex", justifyContent: "center" }}>
                        <Card
                          sx={{
                            width: "100%",
                            maxWidth: 320,
                            bgcolor: "#242424",
                            border: "1px solid rgba(255,255,255,0.03)",
                            borderRadius: 2,
                            overflow: "hidden",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-8px)",
                              boxShadow: "0 12px 24px rgba(196,165,116,0.15)",
                              borderColor: "rgba(196,165,116,0.2)",
                            },
                          }}
                        >
                          {/* Avatar Section */}
                          <Box
                            sx={{
                              height: 180,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "linear-gradient(135deg, #C4A574 0%, #8B7355 100%)",
                              position: "relative",
                              "&::after": {
                                content: '""',
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: "30px",
                                background: "linear-gradient(to top, rgba(36,36,36,0.8), transparent)",
                              }
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 90,
                                height: 90,
                                bgcolor: "rgba(255,255,255,0.2)",
                                fontSize: "2rem",
                                fontWeight: 700,
                                color: "#fff",
                                border: "3px solid rgba(255,255,255,0.3)",
                                backdropFilter: "blur(10px)",
                              }}
                            >
                              {initials}
                            </Avatar>
                          </Box>

                          {/* Content Section */}
                          <CardContent sx={{ p: 2, bgcolor: "#242424" }}>
                            <Typography
                              variant="h6"
                              sx={{
                                color: "#fff",
                                fontWeight: 700,
                                mb: 0.5,
                                textAlign: "center",
                                fontSize: { xs: "1rem", sm: "1.1rem" },
                              }}
                            >
                              {customer.CustomerName}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#C4A574",
                                mb: 0.5,
                                textAlign: "center",
                                fontWeight: 600,
                              }}
                            >
                              Token #{customer.TokenNo}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#bbb",
                                display: "block",
                                textAlign: "center",
                                mb: 1,
                              }}
                            >
                              {customer.PhoneNumber}
                            </Typography>
                            
                            {/* Profession Chip */}
                            <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
                              <Chip
                                label={customer.Profession === "Other" ? customer.ProfessionOther || "Other" : customer.Profession}
                                size="small"
                                sx={{ 
                                  bgcolor: "rgba(196,165,116,0.2)", 
                                  color: "#C4A574",
                                  fontWeight: 600,
                                  border: "1px solid rgba(196,165,116,0.3)",
                                }}
                              />
                            </Box>

                            {/* Balance Display */}
                            <Box sx={{ textAlign: "center", mb: 2 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  fontSize: "0.75rem",
                                  color: "#bbb",
                                  mb: 0.5,
                                }}
                              >
                                Balance
                              </Typography>
                              {(() => {
                                const currentBalance = parseFloat(customer.CurrentBalance) || 0;
                                const availableCredit = parseFloat(schemeCredits[customer.TokenNo]) || 0;
                                const totalBalance = currentBalance - availableCredit;
                                
                                return (
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: "1.2rem",
                                      color: totalBalance > 0 ? "#ff5252" : totalBalance < 0 ? "#4caf50" : "#bbb",
                                    }}
                                  >
                                    ₹{Math.abs(totalBalance).toLocaleString()}
                                    {totalBalance > 0 && (
                                      <Typography component="span" sx={{ fontSize: "0.75rem", ml: 0.5, color: "#ff5252" }}>
                                        (Due)
                                      </Typography>
                                    )}
                                    {totalBalance < 0 && (
                                      <Typography component="span" sx={{ fontSize: "0.75rem", ml: 0.5, color: "#4caf50" }}>
                                        (Credit)
                                      </Typography>
                                    )}
                                  </Typography>
                                );
                              })()}
                            </Box>

                            {/* Coupon Code Display */}
                            {customer.CouponCode && (
                              <Box sx={{ textAlign: "center", mb: 2 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    fontSize: "0.75rem",
                                    color: "#bbb",
                                    mb: 0.5,
                                  }}
                                >
                                  Coupon Code
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: "monospace",
                                    fontWeight: 600,
                                    fontSize: "0.85rem",
                                    color: "#C4A574",
                                    bgcolor: "rgba(196,165,116,0.1)",
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    display: "inline-block",
                                    border: "1px solid rgba(196,165,116,0.2)",
                                  }}
                                >
                                  {customer.CouponCode}
                                </Typography>
                              </Box>
                            )}

                            {/* Debug: Show coupon amount even if no code */}
                            {customer.CouponAmount && parseFloat(customer.CouponAmount) > 0 && !customer.CouponCode && (
                              <Box sx={{ textAlign: "center", mb: 2 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    fontSize: "0.75rem",
                                    color: "#ff9800",
                                    mb: 0.5,
                                  }}
                                >
                                  Coupon Amount (No Code)
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.85rem",
                                    color: "#ff9800",
                                    bgcolor: "rgba(255,152,0,0.1)",
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    display: "inline-block",
                                    border: "1px solid rgba(255,152,0,0.2)",
                                  }}
                                >
                                  ₹{parseFloat(customer.CouponAmount).toFixed(2)}
                                </Typography>
                              </Box>
                            )}

                            {/* Action Buttons */}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              {/* First Row: View, Edit, Delete */}
                              <Box sx={{ 
                                display: "grid", 
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: 1,
                              }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<VisibilityIcon sx={{ fontSize: "0.9rem" }} />}
                                  onClick={() => handleView(customer)}
                                  sx={{
                                    bgcolor: "#2196f3",
                                    color: "white",
                                    fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                    px: 1,
                                    py: 0.5,
                                    fontWeight: 600,
                                    "&:hover": { bgcolor: "#1976d2" },
                                  }}
                                >
                                  View
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<EditIcon sx={{ fontSize: "0.9rem" }} />}
                                  onClick={() => handleEdit(customer)}
                                  sx={{
                                    bgcolor: "#ff9800",
                                    color: "white",
                                    fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                    px: 1,
                                    py: 0.5,
                                    fontWeight: 600,
                                    "&:hover": { bgcolor: "#f57c00" },
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<DeleteIcon sx={{ fontSize: "0.9rem" }} />}
                                  onClick={() => handleDelete(customer)}
                                  sx={{
                                    bgcolor: "#f44336",
                                    color: "white",
                                    fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                    px: 1,
                                    py: 0.5,
                                    fontWeight: 600,
                                    "&:hover": { bgcolor: "#d32f2f" },
                                  }}
                                >
                                  Delete
                                </Button>
                              </Box>
                              
                              {/* Second Row: Payment, Scheme */}
                              <Box sx={{ 
                                display: "flex",
                                justifyContent: "center",
                                gap: 1,
                              }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<span style={{ fontSize: "0.9rem" }}>₹</span>}
                                  onClick={() => navigate(`/admin/customers/${customer.TokenNo}/payments`)}
                                  sx={{
                                    bgcolor: "#4caf50",
                                    color: "white",
                                    fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                    px: 1.5,
                                    py: 0.5,
                                    fontWeight: 600,
                                    "&:hover": { bgcolor: "#388e3c" },
                                  }}
                                >
                                  Payment
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<span style={{ fontSize: "0.9rem" }}>📋</span>}
                                  onClick={() => navigate(`/admin/customers/${customer.TokenNo}/schemes`)}
                                  sx={{
                                    bgcolor: "#9c27b0",
                                    color: "white",
                                    fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                    px: 1.5,
                                    py: 0.5,
                                    fontWeight: 600,
                                    "&:hover": { bgcolor: "#7b1fa2" },
                                  }}
                                >
                                  Scheme
                                </Button>
                              </Box>
                              
                              {/* Third Row: Opening Balance/Advance */}
                              <Box sx={{ 
                                display: "flex",
                                justifyContent: "center",
                              }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<AccountBalanceIcon sx={{ fontSize: "0.9rem" }} />}
                                  onClick={() => handleOpeningBalanceClick(customer)}
                                  sx={{
                                    bgcolor: "#ff5722",
                                    color: "white",
                                    fontSize: { xs: "0.6rem", sm: "0.65rem" },
                                    px: 1,
                                    py: 0.5,
                                    fontWeight: 600,
                                    "&:hover": { bgcolor: "#e64a19" },
                                  }}
                                >
                                  Opening Balance / Advance
                                </Button>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })
                ) : (
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: "center", py: 8 }}>
                      <Typography variant="h6" sx={{ color: "#bbb", mb: 1 }}>
                        No customers found
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#888" }}>
                        {searchQuery ? "Try adjusting your search criteria" : "Start by adding your first customer"}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Pagination Controls */}
            {!loading && totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 4, gap: 2 }}>
                <Button
                  variant="outlined"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  sx={{ 
                    borderColor: "#C4A574", 
                    color: currentPage === 1 ? "#666" : "#C4A574",
                    "&:hover": { borderColor: "#B39562", bgcolor: "rgba(196, 165, 116, 0.1)" }
                  }}
                >
                  Previous
                </Button>
                
                <Typography sx={{ color: "#fff", mx: 2 }}>
                  Page {currentPage} of {totalPages} ({totalCustomers} customers)
                </Typography>
                
                <Button
                  variant="outlined"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  sx={{ 
                    borderColor: "#C4A574", 
                    color: currentPage === totalPages ? "#666" : "#C4A574",
                    "&:hover": { borderColor: "#B39562", bgcolor: "rgba(196, 165, 116, 0.1)" }
                  }}
                >
                  Next
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Customer View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#242424",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.03)",
            maxHeight: "90vh",
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: "#C4A574", color: "#000", fontWeight: 700, display: "flex", alignItems: "center", gap: 2 }}>
          <VisibilityIcon />
          Customer Details
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#242424", p: 0 }}>
          {viewCustomerLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#C4A574" }} />
            </Box>
          ) : selectedCustomerForView ? (
            <Box sx={{ p: 3 }}>
              {/* Customer Header */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 4, p: 3, bgcolor: "#1a1a1a", borderRadius: 2, border: "1px solid rgba(255,255,255,0.06)" }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: getAvatarColor(selectedCustomerForView.CustomerName),
                    fontSize: "2rem",
                    fontWeight: 700,
                  }}
                >
                  {getInitials(selectedCustomerForView.CustomerName)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
                    {selectedCustomerForView.CustomerName}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#C4A574", fontWeight: 600, mb: 0.5 }}>
                    Token #{selectedCustomerForView.TokenNo}
                  </Typography>
                  <Chip
                    label={selectedCustomerForView.Profession === "Other" ? selectedCustomerForView.ProfessionOther || "Other" : selectedCustomerForView.Profession}
                    size="small"
                    sx={{ 
                      bgcolor: "rgba(196,165,116,0.2)", 
                      color: "#C4A574",
                      fontWeight: 600,
                      border: "1px solid rgba(196,165,116,0.3)",
                    }}
                  />
                </Box>
              </Box>

              {/* Customer Information Grid */}
              <Grid container spacing={3}>
                {/* Personal Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ color: "#C4A574", fontWeight: 700, mb: 2, borderBottom: "2px solid #C4A574", pb: 1 }}>
                    Personal Information
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Phone Number</Typography>
                      <Typography variant="body1" sx={{ color: "#fff", fontWeight: 600 }}>
                        {selectedCustomerForView.PhoneNumber || "Not provided"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Email</Typography>
                      <Typography variant="body1" sx={{ color: "#fff", fontWeight: 600 }}>
                        {selectedCustomerForView.EmailID || "Not provided"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Birthday</Typography>
                      <Typography variant="body1" sx={{ color: "#fff", fontWeight: 600 }}>
                        {selectedCustomerForView.Birthday ? new Date(selectedCustomerForView.Birthday).toLocaleDateString() : "Not provided"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Age</Typography>
                      <Typography variant="body1" sx={{ color: "#fff", fontWeight: 600 }}>
                        {selectedCustomerForView.Age || "Not provided"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Marital Status</Typography>
                      <Typography variant="body1" sx={{ color: "#fff", fontWeight: 600 }}>
                        {selectedCustomerForView.IsMarried === 1 ? "Married" : "Single"}
                      </Typography>
                    </Box>
                    {selectedCustomerForView.IsMarried === 1 && selectedCustomerForView.AnniversaryDate && (
                      <Box>
                        <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Anniversary Date</Typography>
                        <Typography variant="body1" sx={{ color: "#fff", fontWeight: 600 }}>
                          {new Date(selectedCustomerForView.AnniversaryDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Financial Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ color: "#C4A574", fontWeight: 700, mb: 2, borderBottom: "2px solid #C4A574", pb: 1 }}>
                    Financial Information
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Current Balance</Typography>
                      <Typography variant="h6" sx={{ 
                        color: (selectedCustomerForView.CurrentBalance || 0) > 0 ? "#ff5252" : (selectedCustomerForView.CurrentBalance || 0) < 0 ? "#4caf50" : "#bbb",
                        fontWeight: 700 
                      }}>
                        ₹{Math.abs(selectedCustomerForView.CurrentBalance || 0).toLocaleString()}
                        {(selectedCustomerForView.CurrentBalance || 0) > 0 && " (Due)"}
                        {(selectedCustomerForView.CurrentBalance || 0) < 0 && " (Credit)"}
                      </Typography>
                    </Box>
                    {selectedCustomerForView.OpeningBalance && parseFloat(selectedCustomerForView.OpeningBalance) > 0 ? (
                      <Box>
                        <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Opening Balance</Typography>
                        <Typography variant="body1" sx={{ color: "#ff5252", fontWeight: 600 }}>
                          ₹{Math.abs(selectedCustomerForView.OpeningBalance).toLocaleString()}
                        </Typography>
                      </Box>
                    ) : null}
                    {selectedCustomerForView.Advance && parseFloat(selectedCustomerForView.Advance) > 0 ? (
                      <Box>
                        <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Advance Payment</Typography>
                        <Typography variant="body1" sx={{ color: "#4caf50", fontWeight: 600 }}>
                          ₹{Math.abs(selectedCustomerForView.Advance).toLocaleString()}
                        </Typography>
                      </Box>
                    ) : null}
                    {schemeCredits[selectedCustomerForView.TokenNo] && parseFloat(schemeCredits[selectedCustomerForView.TokenNo]) > 0 ? (
                      <Box>
                        <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Available Scheme Credit</Typography>
                        <Typography variant="body1" sx={{ color: "#4caf50", fontWeight: 600 }}>
                          ₹{Math.abs(schemeCredits[selectedCustomerForView.TokenNo]).toLocaleString()}
                        </Typography>
                      </Box>
                    ) : null}
                    {selectedCustomerForView.CouponCode && (
                      <Box>
                        <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Coupon Code</Typography>
                        <Typography variant="body1" sx={{ 
                          color: "#C4A574", 
                          fontWeight: 600,
                          fontFamily: "monospace",
                          bgcolor: "rgba(196,165,116,0.1)",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          display: "inline-block",
                          border: "1px solid rgba(196,165,116,0.2)",
                        }}>
                          {selectedCustomerForView.CouponCode}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Complete Measurements */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: "#C4A574", fontWeight: 700, mb: 2, borderBottom: "2px solid #C4A574", pb: 1 }}>
                    Complete Measurements (in inches)
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Upper Body */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: "#1a1a1a", borderRadius: 1, border: "1px solid rgba(255,255,255,0.06)", height: "100%" }}>
                        <Typography variant="subtitle2" sx={{ color: "#C4A574", fontWeight: 600, mb: 1.5 }}>Upper Body</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Shoulder: <span style={{ color: "#fff" }}>{selectedCustomerForView.Shoulder ? `${selectedCustomerForView.Shoulder}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Shoulder Front Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.ShoulderFrontLength ? `${selectedCustomerForView.ShoulderFrontLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Shoulder Front Width: <span style={{ color: "#fff" }}>{selectedCustomerForView.ShoulderFrontWidth ? `${selectedCustomerForView.ShoulderFrontWidth}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Shoulder Back Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.ShoulderBackLength ? `${selectedCustomerForView.ShoulderBackLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Shoulder Back Width: <span style={{ color: "#fff" }}>{selectedCustomerForView.ShoulderBackWidth ? `${selectedCustomerForView.ShoulderBackWidth}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Neck Straight Front: <span style={{ color: "#fff" }}>{selectedCustomerForView.NeckStraightFront ? `${selectedCustomerForView.NeckStraightFront}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Neck Straight Back: <span style={{ color: "#fff" }}>{selectedCustomerForView.NeckStraightBack ? `${selectedCustomerForView.NeckStraightBack}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Neck Slight Front: <span style={{ color: "#fff" }}>{selectedCustomerForView.NeckSlightFront ? `${selectedCustomerForView.NeckSlightFront}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Neck Slight Back: <span style={{ color: "#fff" }}>{selectedCustomerForView.NeckSlightBack ? `${selectedCustomerForView.NeckSlightBack}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Collar: <span style={{ color: "#fff" }}>{selectedCustomerForView.Collar ? `${selectedCustomerForView.Collar}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Half Stand: <span style={{ color: "#fff" }}>{selectedCustomerForView.HalfStand ? `${selectedCustomerForView.HalfStand}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Designer Back Deep: <span style={{ color: "#fff" }}>{selectedCustomerForView.DesignerBackDeep ? `${selectedCustomerForView.DesignerBackDeep}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Designer Back Pack: <span style={{ color: "#fff" }}>{selectedCustomerForView.DesignerBackPack ? `${selectedCustomerForView.DesignerBackPack}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Designer Back Deep Highest: <span style={{ color: "#fff" }}>{selectedCustomerForView.DesignerBackDeepHighest ? `${selectedCustomerForView.DesignerBackDeepHighest}"` : "Not set"}</span>
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Sleeves */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: "#1a1a1a", borderRadius: 1, border: "1px solid rgba(255,255,255,0.06)", height: "100%" }}>
                        <Typography variant="subtitle2" sx={{ color: "#C4A574", fontWeight: 600, mb: 1.5 }}>Sleeves</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Sleeve Short Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.SleeveShortLength ? `${selectedCustomerForView.SleeveShortLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Sleeve Short Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.SleeveShortRound ? `${selectedCustomerForView.SleeveShortRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Sleeve Above Elbow Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.SleeveAboveElbowLength ? `${selectedCustomerForView.SleeveAboveElbowLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Sleeve Above Elbow Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.SleeveAboveElbowRound ? `${selectedCustomerForView.SleeveAboveElbowRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Sleeve 3/4 Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.Sleeve34Length ? `${selectedCustomerForView.Sleeve34Length}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Sleeve 3/4 Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.Sleeve34Round ? `${selectedCustomerForView.Sleeve34Round}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Sleeve Full Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.SleeveFullLength ? `${selectedCustomerForView.SleeveFullLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Sleeve Full Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.SleeveFullRound ? `${selectedCustomerForView.SleeveFullRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Armhole Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.ArmholeRound ? `${selectedCustomerForView.ArmholeRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Biceps Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.BicepsLength ? `${selectedCustomerForView.BicepsLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Biceps Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.BicepsRound ? `${selectedCustomerForView.BicepsRound}"` : "Not set"}</span>
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Bust & Waist */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: "#1a1a1a", borderRadius: 1, border: "1px solid rgba(255,255,255,0.06)", height: "100%" }}>
                        <Typography variant="subtitle2" sx={{ color: "#C4A574", fontWeight: 600, mb: 1.5 }}>Bust & Waist</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Upper Bust Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.UpperBustLength ? `${selectedCustomerForView.UpperBustLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Upper Bust Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.UpperBustRound ? `${selectedCustomerForView.UpperBustRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Gap: <span style={{ color: "#fff" }}>{selectedCustomerForView.Gap ? `${selectedCustomerForView.Gap}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Bust Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.BustLength ? `${selectedCustomerForView.BustLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Bust Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.BustRound ? `${selectedCustomerForView.BustRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Under Bust Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.UnderBustLength ? `${selectedCustomerForView.UnderBustLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Under Bust Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.UnderBustRound ? `${selectedCustomerForView.UnderBustRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Stomach Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.StomachLength ? `${selectedCustomerForView.StomachLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Stomach Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.StomachRound ? `${selectedCustomerForView.StomachRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Lower Waist Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.LowerWaistLength ? `${selectedCustomerForView.LowerWaistLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Lower Waist Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.LowerWaistRound ? `${selectedCustomerForView.LowerWaistRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Hip Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.HipLength ? `${selectedCustomerForView.HipLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Hip Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.HipRound ? `${selectedCustomerForView.HipRound}"` : "Not set"}</span>
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Lower Body */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: "#1a1a1a", borderRadius: 1, border: "1px solid rgba(255,255,255,0.06)", height: "100%" }}>
                        <Typography variant="subtitle2" sx={{ color: "#C4A574", fontWeight: 600, mb: 1.5 }}>Lower Body</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Lower Total Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.LowerTotalLength ? `${selectedCustomerForView.LowerTotalLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Plus Heels: <span style={{ color: "#fff" }}>{selectedCustomerForView.PlusHeels ? `${selectedCustomerForView.PlusHeels}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Thighs Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.ThighsLength ? `${selectedCustomerForView.ThighsLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Thighs Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.ThighsRound ? `${selectedCustomerForView.ThighsRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Knees Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.KneesLength ? `${selectedCustomerForView.KneesLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Knees Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.KneesRound ? `${selectedCustomerForView.KneesRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Calf Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.CalfLength ? `${selectedCustomerForView.CalfLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Calf Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.CalfRound ? `${selectedCustomerForView.CalfRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Bottom Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.BottomLength ? `${selectedCustomerForView.BottomLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Bottom Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.BottomRound ? `${selectedCustomerForView.BottomRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Seat Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.SeatLength ? `${selectedCustomerForView.SeatLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Seat Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.SeatRound ? `${selectedCustomerForView.SeatRound}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Ankle Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.AnkleLength ? `${selectedCustomerForView.AnkleLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Ankle Round: <span style={{ color: "#fff" }}>{selectedCustomerForView.AnkleRound ? `${selectedCustomerForView.AnkleRound}"` : "Not set"}</span>
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Length Measurements */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: "#1a1a1a", borderRadius: 1, border: "1px solid rgba(255,255,255,0.06)", height: "100%" }}>
                        <Typography variant="subtitle2" sx={{ color: "#C4A574", fontWeight: 600, mb: 1.5 }}>Length Measurements</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Above Knee: <span style={{ color: "#fff" }}>{selectedCustomerForView.AboveKnee ? `${selectedCustomerForView.AboveKnee}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Below Knee: <span style={{ color: "#fff" }}>{selectedCustomerForView.BelowKnee ? `${selectedCustomerForView.BelowKnee}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Middle Floor Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.MiddleFloorLength ? `${selectedCustomerForView.MiddleFloorLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Full Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.FullLength ? `${selectedCustomerForView.FullLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Ankle Length Measure: <span style={{ color: "#fff" }}>{selectedCustomerForView.AnkleLengthMeasure ? `${selectedCustomerForView.AnkleLengthMeasure}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Floor Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.FloorLength ? `${selectedCustomerForView.FloorLength}"` : "Not set"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Plus Heels Length: <span style={{ color: "#fff" }}>{selectedCustomerForView.PlusHeelsLength ? `${selectedCustomerForView.PlusHeelsLength}"` : "Not set"}</span>
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Additional Information */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: "#1a1a1a", borderRadius: 1, border: "1px solid rgba(255,255,255,0.06)", height: "100%" }}>
                        <Typography variant="subtitle2" sx={{ color: "#C4A574", fontWeight: 600, mb: 1.5 }}>Additional Information</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Fitting Preference: <span style={{ color: "#fff" }}>{selectedCustomerForView.FittingPreference || "Regular"}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>
                            Lower Description: <span style={{ color: "#fff" }}>{selectedCustomerForView.LowerDescription || "Not set"}</span>
                          </Typography>
                          {selectedCustomerForView.SpecialNotes && (
                            <Typography variant="caption" sx={{ color: "#bbb", display: "block", mt: 1, fontStyle: "italic" }}>
                              Special Notes: <span style={{ color: "#fff" }}>{selectedCustomerForView.SpecialNotes}</span>
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <Typography variant="h6" sx={{ color: "#bbb" }}>
                Failed to load customer details
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#242424", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <Button 
            onClick={() => setViewDialogOpen(false)} 
            sx={{ color: "#bbb" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Opening Balance/Advance Dialog */}
      <Dialog
        open={openingBalanceDialogOpen}
        onClose={() => setOpeningBalanceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#242424",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.03)",
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: "#C4A574", color: "#000", fontWeight: 700 }}>
          {selectedCustomerForBalance && (
            (selectedCustomerForBalance.OpeningBalance && parseFloat(selectedCustomerForBalance.OpeningBalance) !== 0) ||
            (selectedCustomerForBalance.Advance && parseFloat(selectedCustomerForBalance.Advance) !== 0)
          ) ? "Edit Balance Entry" : "Set Opening Balance / Advance"}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#242424" }}>
          <Box sx={{ pt: 2 }}>
            {selectedCustomerForBalance && (
              <Box sx={{ mb: 3, p: 2, bgcolor: "#1a1a1a", borderRadius: 1, border: "1px solid rgba(255,255,255,0.06)" }}>
                <Typography variant="body2" sx={{ color: "#fff", mb: 1 }}>
                  <strong>Customer:</strong> {selectedCustomerForBalance.CustomerName}
                </Typography>
                <Typography variant="body2" sx={{ color: "#fff", mb: 1 }}>
                  <strong>Token No:</strong> {selectedCustomerForBalance.TokenNo}
                </Typography>
                <Typography variant="body2" sx={{ color: "#fff", mb: 1 }}>
                  <strong>Current Balance:</strong> ₹{Math.abs(selectedCustomerForBalance.CurrentBalance || 0).toLocaleString()} 
                  {(selectedCustomerForBalance.CurrentBalance || 0) > 0 ? " (Due)" : (selectedCustomerForBalance.CurrentBalance || 0) < 0 ? " (Credit)" : ""}
                </Typography>
                {/* Show existing balance info */}
                {selectedCustomerForBalance.OpeningBalance && parseFloat(selectedCustomerForBalance.OpeningBalance) !== 0 && (
                  <Typography variant="body2" sx={{ color: "#ff5252", fontWeight: 600 }}>
                    <strong>Current Opening Balance:</strong> ₹{Math.abs(selectedCustomerForBalance.OpeningBalance).toLocaleString()}
                  </Typography>
                )}
                {selectedCustomerForBalance.Advance && parseFloat(selectedCustomerForBalance.Advance) !== 0 && (
                  <Typography variant="body2" sx={{ color: "#4caf50", fontWeight: 600 }}>
                    <strong>Current Advance:</strong> ₹{Math.abs(selectedCustomerForBalance.Advance).toLocaleString()}
                  </Typography>
                )}
                {/* Show info about deletion */}
                {((selectedCustomerForBalance.OpeningBalance && parseFloat(selectedCustomerForBalance.OpeningBalance) !== 0) ||
                  (selectedCustomerForBalance.Advance && parseFloat(selectedCustomerForBalance.Advance) !== 0)) && (
                  <Typography variant="caption" sx={{ color: "#bbb", fontStyle: "italic", display: "block", mt: 1 }}>
                    You can edit the amount, switch between types, or delete the entry completely.
                  </Typography>
                )}
              </Box>
            )}
            
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend" sx={{ fontWeight: 600, color: "#C4A574" }}>
                Select Type
              </FormLabel>
              <RadioGroup
                value={openingBalanceForm.type}
                onChange={(e) => {
                  const newType = e.target.value;
                  let newAmount = "";
                  
                  // When switching type, show the current amount of that type
                  if (selectedCustomerForBalance) {
                    if (newType === "opening_balance" && selectedCustomerForBalance.OpeningBalance) {
                      newAmount = selectedCustomerForBalance.OpeningBalance.toString();
                    } else if (newType === "advance" && selectedCustomerForBalance.Advance) {
                      newAmount = selectedCustomerForBalance.Advance.toString();
                    }
                  }
                  
                  setOpeningBalanceForm({ type: newType, amount: newAmount });
                }}
                row
              >
                <FormControlLabel 
                  value="opening_balance" 
                  control={<Radio sx={{ color: "#C4A574" }} />} 
                  label={<Typography sx={{ color: "#fff" }}>Opening Balance (Add to Due)</Typography>}
                />
                <FormControlLabel 
                  value="advance" 
                  control={<Radio sx={{ color: "#C4A574" }} />} 
                  label={<Typography sx={{ color: "#fff" }}>Advance (Subtract from Due)</Typography>}
                />
              </RadioGroup>
            </FormControl>
            
            <TextField
              label="Amount (₹)"
              type="number"
              value={openingBalanceForm.amount}
              onChange={(e) => setOpeningBalanceForm({ ...openingBalanceForm, amount: e.target.value })}
              fullWidth
              required
              slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
              helperText={
                openingBalanceForm.type === "opening_balance" 
                  ? "This amount will be added to the customer's due amount. Any existing advance will be cleared."
                  : "This amount will be subtracted from the customer's due amount. Any existing opening balance will be cleared."
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#1a1a1a",
                  borderRadius: 1,
                  border: "1px solid rgba(196,165,116,0.06)",
                  color: "#fff",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "rgba(196,165,116,0.12)" },
                  "&.Mui-focused fieldset": { borderColor: "#C4A574" },
                },
                "& .MuiInputBase-input": { color: "#fff" },
                "& .MuiFormLabel-root": { color: "#C4A574" },
                "& .MuiFormHelperText-root": { color: "#bbb" },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between", bgcolor: "#242424" }}>
          <Box>
            {/* Show delete button only if there's an existing balance entry */}
            {selectedCustomerForBalance && (
              (selectedCustomerForBalance.OpeningBalance && parseFloat(selectedCustomerForBalance.OpeningBalance) !== 0) ||
              (selectedCustomerForBalance.Advance && parseFloat(selectedCustomerForBalance.Advance) !== 0)
            ) && (
              <Button 
                onClick={handleDeleteBalance}
                variant="outlined"
                startIcon={<DeleteIcon />}
                sx={{ 
                  color: "#ff5252", 
                  borderColor: "#ff5252",
                  "&:hover": { 
                    bgcolor: "rgba(255,82,82,0.1)",
                    borderColor: "#ff1744"
                  }
                }}
              >
                Delete Entry
              </Button>
            )}
          </Box>
          
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button 
              onClick={() => setOpeningBalanceDialogOpen(false)} 
              sx={{ color: "#bbb" }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleOpeningBalanceSubmit} 
              variant="contained" 
              sx={{ bgcolor: "#C4A574", color: "#000", fontWeight: 700, "&:hover": { bgcolor: "#B39562" } }}
            >
              {((selectedCustomerForBalance?.OpeningBalance && parseFloat(selectedCustomerForBalance.OpeningBalance) !== 0) ||
                (selectedCustomerForBalance?.Advance && parseFloat(selectedCustomerForBalance.Advance) !== 0)) 
                ? "Update Balance" 
                : (openingBalanceForm.type === "opening_balance" ? "Set Opening Balance" : "Record Advance")}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#242424",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.03)",
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: "#ff5252", color: "#fff", fontWeight: 700, display: "flex", alignItems: "center", gap: 2 }}>
          <DeleteIcon />
          Delete Customer
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#242424", pt: 3 }}>
          {selectedCustomerForDelete && (
            <Box>
              <Typography variant="h6" sx={{ color: "#fff", mb: 2, textAlign: "center" }}>
                Are you sure you want to delete this customer?
              </Typography>
              
              <Box sx={{ p: 3, bgcolor: "#1a1a1a", borderRadius: 2, border: "1px solid rgba(255,255,255,0.06)", mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      bgcolor: getAvatarColor(selectedCustomerForDelete.CustomerName),
                      fontSize: "1.5rem",
                      fontWeight: 700,
                    }}
                  >
                    {getInitials(selectedCustomerForDelete.CustomerName)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
                      {selectedCustomerForDelete.CustomerName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#C4A574", fontWeight: 600 }}>
                      Token #{selectedCustomerForDelete.TokenNo}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#bbb" }}>
                      {selectedCustomerForDelete.PhoneNumber}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 2, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                  <Typography variant="body2" sx={{ color: "#bbb" }}>
                    Current Balance:
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: (selectedCustomerForDelete.CurrentBalance || 0) > 0 ? "#ff5252" : (selectedCustomerForDelete.CurrentBalance || 0) < 0 ? "#4caf50" : "#bbb",
                    fontWeight: 600 
                  }}>
                    ₹{Math.abs(selectedCustomerForDelete.CurrentBalance || 0).toLocaleString()}
                    {(selectedCustomerForDelete.CurrentBalance || 0) > 0 && " (Due)"}
                    {(selectedCustomerForDelete.CurrentBalance || 0) < 0 && " (Credit)"}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: 2, bgcolor: "rgba(255,82,82,0.1)", borderRadius: 1, border: "1px solid rgba(255,82,82,0.3)" }}>
                <Typography variant="body2" sx={{ color: "#ff5252", fontWeight: 600, textAlign: "center" }}>
                  ⚠️ Warning: This action cannot be undone!
                </Typography>
                <Typography variant="caption" sx={{ color: "#bbb", display: "block", textAlign: "center", mt: 1 }}>
                  All customer data, measurements, payments, and history will be permanently deleted.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: "#242424", borderTop: "1px solid rgba(255,255,255,0.06)", justifyContent: "space-between" }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            variant="outlined"
            sx={{ 
              color: "#bbb", 
              borderColor: "#bbb",
              "&:hover": { 
                bgcolor: "rgba(255,255,255,0.05)",
                borderColor: "#fff"
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete}
            variant="contained" 
            startIcon={<DeleteIcon />}
            sx={{ 
              bgcolor: "#ff5252", 
              color: "#fff", 
              fontWeight: 700,
              "&:hover": { bgcolor: "#ff1744" }
            }}
          >
            Delete Customer
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}
